import { useState } from 'react';
import { useDevice, useRevokeDevice, useRemoveDevice, formatDateTime } from '@redbax/shared';
import type { DeviceDetail } from '@redbax/shared';
import {
  AnimatedTimestamp,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  Badge,
  Button,
  LoadingSkeleton,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from '@redbax/ui-kit';
import { CheckCircle2, MinusCircle, Copy, Check, ShieldOff, Trash2 } from 'lucide-react';

interface DeviceDetailsDialogProps {
  deviceId: string | null;
  open: boolean;
  onClose: () => void;
}

function StatusBadge({ status }: { status: string }) {
  return (
    <Badge variant={status === 'Online' ? 'success' : 'secondary'}>
      {status === 'Online' ? (
        <CheckCircle2 className="mr-1 h-3 w-3" />
      ) : (
        <MinusCircle className="mr-1 h-3 w-3" />
      )}
      {status}
    </Badge>
  );
}

function CopyButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button
      onClick={handleCopy}
      className="ml-2 inline-flex items-center rounded p-1 text-text-secondary hover:text-text-primary hover:bg-surface-raised transition-colors"
    >
      {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
    </button>
  );
}

function DetailRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex justify-between items-start py-2 border-b border-border last:border-0">
      <span className="text-sm text-text-secondary">{label}</span>
      <span className="text-sm text-text-primary text-right">{children}</span>
    </div>
  );
}

const revocableStatuses = new Set(['Online', 'Offline', 'Approved']);
const removableStatuses = new Set(['Revoked', 'Offline', 'Rejected', 'PendingApproval']);

function RevokeDeviceSection({ device, onRevoked }: { device: DeviceDetail; onRevoked: () => void }) {
  const revokeDevice = useRevokeDevice();
  const [confirmRevoke, setConfirmRevoke] = useState(false);

  if (!revocableStatuses.has(device.status)) return null;

  if (confirmRevoke) {
    return (
      <div className="mt-4 rounded-md border border-status-error/30 bg-status-error/5 p-3">
        <p className="text-sm text-text-secondary">
          This will revoke <strong>{device.hostname}</strong> and disconnect its agent from the platform.
        </p>
        <div className="mt-3 flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={() => setConfirmRevoke(false)}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            size="sm"
            disabled={revokeDevice.isPending}
            onClick={() => {
              revokeDevice.mutate(device.id, {
                onSuccess: () => onRevoked(),
              });
            }}
          >
            {revokeDevice.isPending ? 'Revoking…' : 'Confirm Revoke'}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-4">
      <Button
        variant="outline"
        size="sm"
        className="w-full text-status-error border-status-error/30 hover:bg-status-error/10"
        onClick={() => setConfirmRevoke(true)}
      >
        <ShieldOff className="mr-2 h-4 w-4" />
        Revoke Device
      </Button>
    </div>
  );
}

function RemoveDeviceSection({ device, onRemoved }: { device: DeviceDetail; onRemoved: () => void }) {
  const removeDevice = useRemoveDevice();
  const [confirmRemove, setConfirmRemove] = useState(false);

  if (!removableStatuses.has(device.status)) return null;

  if (confirmRemove) {
    return (
      <div className="mt-4 rounded-md border border-status-error/30 bg-status-error/5 p-3">
        <p className="text-sm text-text-secondary">
          This will permanently remove <strong>{device.hostname}</strong> and disconnect its agent.
        </p>
        <div className="mt-3 flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={() => setConfirmRemove(false)}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            size="sm"
            disabled={removeDevice.isPending}
            onClick={() => {
              removeDevice.mutate(device.id, {
                onSuccess: () => onRemoved(),
              });
            }}
          >
            {removeDevice.isPending ? 'Removing…' : 'Confirm Remove'}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-4">
      <Button
        variant="outline"
        size="sm"
        className="w-full text-status-error border-status-error/30 hover:bg-status-error/10"
        onClick={() => setConfirmRemove(true)}
      >
        <Trash2 className="mr-2 h-4 w-4" />
        Remove Device
      </Button>
    </div>
  );
}

function DeviceDetailContent({ device, onRemoved }: { device: DeviceDetail; onRemoved: () => void }) {
  return (
    <Tabs defaultValue="overview">
      <TabsList className="w-full">
        <TabsTrigger value="overview">Overview</TabsTrigger>
        <TabsTrigger value="identity">Identity</TabsTrigger>
        <TabsTrigger value="activity">Activity</TabsTrigger>
      </TabsList>

      <TabsContent value="overview">
        <div className="rounded-md border border-border bg-surface-secondary p-3">
          <DetailRow label="Status">
            <StatusBadge status={device.status} />
          </DetailRow>
          <DetailRow label="Hostname">{device.hostname}</DetailRow>
          <DetailRow label="Platform">{device.platform}</DetailRow>
          <DetailRow label="Agent Version">{device.agentVersion ?? 'Unknown'}</DetailRow>
        </div>
        <RevokeDeviceSection device={device} onRevoked={onRemoved} />
        <RemoveDeviceSection device={device} onRemoved={onRemoved} />
      </TabsContent>

      <TabsContent value="identity">
        <div className="rounded-md border border-border bg-surface-secondary p-3">
          <DetailRow label="Device ID">
            <span className="font-mono text-xs">{device.id}</span>
            <CopyButton value={device.id} />
          </DetailRow>
          {device.machineGuid && (
            <DetailRow label="Machine GUID">
              <span className="font-mono text-xs">{device.machineGuid}</span>
              <CopyButton value={device.machineGuid} />
            </DetailRow>
          )}
          {device.macAddress && (
            <DetailRow label="MAC Address">
              <span className="font-mono text-xs">{device.macAddress}</span>
            </DetailRow>
          )}
          {device.previousDeviceId && (
            <DetailRow label="Previous Device ID">
              <span className="font-mono text-xs">{device.previousDeviceId}</span>
            </DetailRow>
          )}
        </div>
      </TabsContent>

      <TabsContent value="activity">
        <div className="rounded-md border border-border bg-surface-secondary p-3">
          <DetailRow label="Last Heartbeat">
            <AnimatedTimestamp value={device.lastHeartbeatAt} fallback="Never" />
          </DetailRow>
          <DetailRow label="Registered">{formatDateTime(device.registeredAt)}</DetailRow>
          <DetailRow label="Last Updated">{formatDateTime(device.updatedAt)}</DetailRow>
        </div>
      </TabsContent>
    </Tabs>
  );
}

export function DeviceDetailsDialog({ deviceId, open, onClose }: DeviceDetailsDialogProps) {
  const { data: device, isLoading } = useDevice(deviceId);

  return (
    <Dialog open={open} onOpenChange={(value) => { if (!value) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{device?.hostname ?? 'Device Details'}</DialogTitle>
          <DialogDescription>
            {device ? `${device.platform} endpoint` : 'Loading device information...'}
          </DialogDescription>
        </DialogHeader>
        {isLoading ? (
          <LoadingSkeleton rows={6} />
        ) : device ? (
          <DeviceDetailContent device={device} onRemoved={onClose} />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
